import React from 'react';
import { Link } from 'react-router-dom';
import {
  INCALL_TIERS,
  EXPERIENCE_RATES,
  TIER_NOTES,
  IMAGES,
} from '../lib/content';

// (etiquette moved to its own /etiquette page)

export default function Rates() {
  return (
    <div>
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-12">
        <div className="font-label text-zb-accent mb-6">Rates &amp; bookings</div>
        <h1 className="font-serif text-5xl md:text-7xl text-zb-ink tracking-tight leading-[0.95]">
          Our time <span className="italic text-zb-brand">together.</span>
        </h1>
        <p className="mt-8 text-zb-ink2 text-lg max-w-2xl leading-relaxed">
          Every rendezvous is crafted around you. All rates in AUD. Bespoke
          arrangements are always welcome.
        </p>
        <p className="mt-4 text-zb-ink2 text-base max-w-2xl leading-relaxed italic">
          Rates for international tours differ. Please{' '}
          <Link
            to="/contact"
            data-testid="rates-international-link"
            className="text-zb-brand underline underline-offset-4 hover:text-zb-brandHover not-italic"
          >
            get in touch
          </Link>{' '}
          for exact quotes.
        </p>
        <p
          data-testid="rates-surcharge-note"
          className="mt-3 text-zb-ink2 text-base max-w-2xl leading-relaxed italic"
        >
          Bookings that begin after 9pm and last-minute / rush requests may
          incur a surcharge. I&rsquo;ll always confirm before locking anything in.
        </p>
      </section>

      {/* EXPERIENCES (headline offerings) + sticky image */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-12 md:pb-20 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-8">
          <h2 className="font-serif text-3xl md:text-4xl text-zb-ink mb-8">Experiences</h2>
          <div className="border-t border-zb-border">
            {EXPERIENCE_RATES.map((b, i) => (
              <div
                key={b.title}
                data-testid={`experience-row-${i}`}
                className="grid grid-cols-12 gap-4 py-7 border-b border-zb-border items-baseline zb-fade-up"
                style={{ animationDelay: `${i * 0.05}s` }}
              >
                <div className="col-span-12 md:col-span-5">
                  <div className="font-serif text-2xl md:text-3xl text-zb-ink">{b.title}</div>
                  <div className="font-label text-zb-accent mt-1.5">{b.duration}</div>
                </div>
                <div className="col-span-12 md:col-span-5 text-zb-ink2 text-[14.5px] leading-relaxed">
                  {b.blurb}
                </div>
                <div className="col-span-12 md:col-span-2 md:text-right">
                  <div className="font-serif text-2xl md:text-3xl text-zb-brand">{b.rate}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-5">
            <Link
              to="/contact"
              data-testid="rates-cta-book"
              className="rounded-full bg-zb-brand hover:bg-zb-brandHover text-zb-bg px-8 py-3.5 text-xs tracking-[0.22em] uppercase transition-colors"
            >
              Begin your booking
            </Link>
            <Link
              to="/tour"
              data-testid="rates-cta-tour"
              className="text-zb-ink hover:text-zb-brand text-xs tracking-[0.22em] uppercase underline underline-offset-8 transition-colors"
            >
              Where I&rsquo;m travelling
            </Link>
          </div>
        </div>

        <aside className="md:col-span-4">
          <div className="zb-img-wrap aspect-[3/4] md:sticky md:top-28">
            <img src={IMAGES.ratesAside} alt="" />
          </div>
        </aside>
      </section>

      {/* HOURLY INCALL TIER TABLE */}
      <section className="bg-zb-bg2 border-y border-zb-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="flex items-end justify-between mb-6">
            <h2 className="font-serif text-3xl md:text-4xl text-zb-ink">Incall · by the hour</h2>
            <span className="font-label text-zb-accent hidden md:inline">Melbourne · AUD</span>
          </div>
          <div className="border-t border-zb-border">
            <div className="hidden md:grid grid-cols-12 gap-4 py-4 font-label text-zb-accent border-b border-zb-border">
              <div className="col-span-4">Duration</div>
              <div className="col-span-2 text-right">GFE</div>
              <div className="col-span-2 text-right">NGFE</div>
              <div className="col-span-2 text-right">VIP</div>
              <div className="col-span-2 text-right">Note</div>
            </div>
            {INCALL_TIERS.map((r, i) => {
              const isRecommended = r.duration.toLowerCase().startsWith('2 hour');
              return (
              <div
                key={r.duration}
                data-testid={`incall-row-${i}`}
                className={`grid grid-cols-12 gap-4 py-6 border-b border-zb-border items-baseline zb-fade-up ${
                  isRecommended ? 'bg-zb-brand/5' : ''
                }`}
                style={{ animationDelay: `${i * 0.05}s` }}
              >
                <div className="col-span-12 md:col-span-4">
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="font-serif text-2xl md:text-3xl text-zb-ink">{r.duration}</div>
                    {isRecommended && (
                      <span
                        data-testid="incall-recommended-badge"
                        className="font-label text-[10px] tracking-[0.22em] uppercase text-zb-bg bg-zb-brand px-3 py-1 rounded-full"
                      >
                        Recommended minimum introduction
                      </span>
                    )}
                  </div>
                  {r.note && (
                    <div className="md:hidden font-label text-zb-accent mt-2">{r.note}</div>
                  )}
                </div>
                <div className="col-span-4 md:col-span-2 md:text-right">
                  <div className="md:hidden font-label text-zb-accent">GFE</div>
                  <div className="font-serif text-xl md:text-2xl text-zb-ink">{r.gfe}</div>
                </div>
                <div className="col-span-4 md:col-span-2 md:text-right">
                  <div className="md:hidden font-label text-zb-accent">NGFE</div>
                  <div className="font-serif text-xl md:text-2xl text-zb-ink">{r.ngfe}</div>
                </div>
                <div className="col-span-4 md:col-span-2 md:text-right">
                  <div className="md:hidden font-label text-zb-accent">VIP</div>
                  <div className="font-serif text-xl md:text-2xl text-zb-brand">{r.vip}</div>
                </div>
                <div className="hidden md:block col-span-2 md:text-right text-xs text-zb-accent italic">
                  {r.note}
                </div>
              </div>
              );
            })}
          </div>

          {/* TIER NOTES */}
          <div className="mt-16 grid md:grid-cols-3 gap-10">
            {TIER_NOTES.map(([t, d]) => (
              <div key={t}>
                <div className="font-serif text-2xl text-zb-ink mb-3">{t}</div>
                <p className="text-zb-ink2 text-base leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES philosophy (no list) */}
      <section>
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-14 md:py-20 text-center">
          <div className="font-label text-zb-accent mb-4">A word on services</div>
          <h2 className="font-serif text-4xl md:text-6xl text-zb-ink leading-tight mb-6">
            Intimacy over <span className="italic text-zb-brand">checklists.</span>
          </h2>
          <p className="text-zb-ink2 text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
            A guide, not a menu. Chemistry isn&rsquo;t engineered, and neither
            am I. If there&rsquo;s something you&rsquo;re curious about, just ask.
          </p>
          <Link
            to="/etiquette"
            data-testid="rates-etiquette-link"
            className="inline-block mt-10 text-zb-ink hover:text-zb-brand text-xs tracking-[0.22em] uppercase underline underline-offset-8 decoration-zb-border hover:decoration-zb-brand transition-colors"
          >
            Read the full etiquette →
          </Link>
        </div>
      </section>
    </div>
  );
}
